import { useEffect, useState } from "react";
import { TelegramSettings } from "../components/TelegramSettings";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Save, User, Send, Users, GraduationCap, UsersRound, HeartPulse, Link2 } from "lucide-react";

const FIELDS = [
  { key: "link_canale_ricette", label: "Canale Telegram · Benessere & Ricette", hint: "Broadcast contenuti e ricette per i clienti", icon: Send, color: "#229ED9" },
  { key: "link_gruppo_supporto", label: "Gruppo Telegram · Benessere & Supporto", hint: "Community clienti: domande e supporto", icon: Users, color: "#229ED9" },
  { key: "link_canale_formazione", label: "Canale Telegram · Formazione & Business", hint: "Broadcast materiali per nuovi incaricati", icon: GraduationCap, color: "#229ED9" },
  { key: "link_gruppo_team", label: "Gruppo Telegram · Team Collaboratori", hint: "Chat operativa del team", icon: UsersRound, color: "#229ED9" },
  { key: "link_autovalutazione", label: "Autovalutazione Benessere · HgoWeb", hint: "Il tuo link personale al test di autovalutazione", icon: HeartPulse, color: "#10B981" },
];

const empty = () => ({ name: "", link_canale_ricette: "", link_gruppo_supporto: "", link_canale_formazione: "", link_gruppo_team: "", link_autovalutazione: "" });

export function Settings() {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/profile").then((r) => {
      setForm({ ...empty(), ...r.data });
      setLoaded(true);
    });
  }, []);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", form);
      toast.success("I tuoi link sono stati salvati!");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl space-y-5" data-testid="settings-view">
      <div className="glass rounded-2xl p-5 border-l-4" style={{ borderColor: "#10B981" }}>
        <div className="flex items-center gap-2 mb-1">
          <Link2 className="h-5 w-5 text-emerald-400" />
          <h3 className="font-display font-bold">I Miei Link</h3>
        </div>
        <p className="text-xs text-slate-400">
          Salva qui i tuoi riferimenti personali. Tutti i messaggi pronti in <span className="text-emerald-300">Business Wellness → Clienti &amp; Assistenza</span> si
          compileranno in automatico con il tuo nome e i tuoi link. Così ogni collaboratore può duplicare il sistema con i propri recapiti.
        </p>
      </div>

      <div className="glass rounded-2xl p-5 space-y-4">
        <div>
          <label className="text-xs text-slate-400 flex items-center gap-1.5 mb-1.5"><User className="h-3.5 w-3.5" /> Nome e Cognome</label>
          <input data-testid="profile-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} disabled={!loaded}
            placeholder={loaded ? "Es. Elara Fiore" : "Caricamento…"}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-emerald-400/50 disabled:opacity-50" />
        </div>

        {FIELDS.map((f, i) => {
          const Icon = f.icon;
          return (
            <motion.div key={f.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <label className="text-xs text-slate-300 flex items-center gap-1.5 mb-1.5">
                <span className="h-5 w-5 rounded-md grid place-items-center" style={{ background: `${f.color}22` }}><Icon className="h-3 w-3" style={{ color: f.color }} /></span>
                {f.label}
              </label>
              <input data-testid={`profile-${f.key}`} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} disabled={!loaded}
                placeholder="https://t.me/…"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-emerald-400/50 disabled:opacity-50" />
              <p className="text-[11px] text-slate-500 mt-1">{f.hint}</p>
            </motion.div>
          );
        })}

        <button data-testid="save-profile-button" onClick={save} disabled={saving || !loaded}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-400 transition-colors disabled:opacity-60">
          <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva i miei link"}
        </button>
      </div>
      <TelegramSettings />
    </div>
  );
}
